import React, {
  useMemo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import CytoscapeComponent from 'react-cytoscapejs';
import { LoaderScreen } from '@/components/Elements/LoaderScreen';

import { Core } from 'cytoscape';

import {
  NetworkData,
  NodeType,
  MIN_ZOOM,
  MAX_ZOOM,
} from '@/features/connect-dots/types/cytoscape';
import {
  applyCytoscapeStyles,
  setupMultiThemeSpiralLayout,
} from '@/features/connect-dots/styles/networkStyles';
import { NodeTooltip } from '@/features/connect-dots/components/tooltips/NodeTooltip';
import { useNodeTooltip } from '@/features/connect-dots/hooks/useNodeTooltip';
import { useCytoscapeEvents } from '@/features/connect-dots/hooks/useCytoscapeEvents';
import { useNetworkElements } from '@/features/connect-dots/hooks/useNetworkElements';

interface NetworkContainerProps {
  filteredData: NetworkData | null;
  cytoscapeCoreRefObject: React.MutableRefObject<Core | null>;
  selectedNodeIds?: Set<string> | undefined;
}

const NetworkContainer: React.FC<NetworkContainerProps> = ({
  filteredData,
  cytoscapeCoreRefObject,
  selectedNodeIds,
}) => {
  const hasInitialized: React.MutableRefObject<boolean> = useRef(false);

  const [searchedNodeIds] = React.useState<Set<string>>(new Set<string>());
  const fixedNodes = useMemo(() => new Set<string>(), []);
  const elements = useNetworkElements(
    filteredData,
    searchedNodeIds,
    selectedNodeIds,
    fixedNodes
  );

  const isDataReady = useMemo(() => {
    return filteredData && filteredData.nodes && filteredData.nodes.length > 0;
  }, [filteredData]);

  // ローディング状態を管理するステート
  const [isLoading, setIsLoading] = useState(false);

  const setCy = useCallback(
    (cyInstance: Core) => {
      if (!cyInstance || cytoscapeCoreRefObject.current === cyInstance) {
        return;
      }
      cytoscapeCoreRefObject.current = cyInstance;

      setIsLoading(true); // ローディング開始
      applyCytoscapeStyles(cyInstance);
      setIsLoading(false); // レイアウト適用完了後にローディング終了

      cyInstance.resize();
    },
    [cytoscapeCoreRefObject]
  );

  useEffect(() => {
    if (!cytoscapeCoreRefObject.current) {
      return;
    }

    cytoscapeCoreRefObject.current.elements().remove(); // データが準備できていなければ要素をクリア
    if (!isDataReady) {
      return;
    }

    cytoscapeCoreRefObject.current.add(elements); // 新しい要素を追加

    // ノーマルタグノードを非表示にする
    cytoscapeCoreRefObject.current
      .nodes(`[nodeType = "${NodeType.NORMAL}"]`)
      .style('display', 'none');

    // .selected クラスを持つノーマルタグノードは表示する
    if (selectedNodeIds && selectedNodeIds.size > 0) {
      selectedNodeIds.forEach((nodeId) => {
        const node = cytoscapeCoreRefObject.current?.getElementById(nodeId);
        if (node && node.data('nodeType') === NodeType.NORMAL) {
          node.style('display', 'element');
          node.addClass('selected');
        }
      });
    }

    // レイアウトの適用
    setupMultiThemeSpiralLayout(cytoscapeCoreRefObject);

    // 初回のみ初期ズームレベルを設定
    if (!hasInitialized.current) {
      setTimeout(() => {
        cytoscapeCoreRefObject.current?.zoom(0.5); // 初期ズームレベルを設定
        cytoscapeCoreRefObject.current?.center();
        hasInitialized.current = true; // 初回描画フラグを立てる
      }, 200); // 少し遅延を入れてからズームとセンタリングを適用
    }
  }, [cytoscapeCoreRefObject, isDataReady, elements, selectedNodeIds]);

  const { tooltipState, showTooltip, hideTooltip } = useNodeTooltip();

  useCytoscapeEvents({
    cytoscapeCoreRefObject,
    showTooltip,
    hideTooltip,
  });

  return (
    <div
      className="size-full"
      style={{
        minHeight: '80vh',
        maxHeight: 'calc(100vh - 8px)',
        minWidth: '300px',
        position: 'relative', // ローディング表示のために必要
      }}
    >
      {isLoading && <LoaderScreen contained={true} />}
      <CytoscapeComponent
        elements={isDataReady ? elements : []}
        style={{ width: '100%', height: 'calc(100vh - 8px)' }}
        panningEnabled={true}
        zoomingEnabled={true}
        minZoom={MIN_ZOOM}
        maxZoom={MAX_ZOOM}
        boxSelectionEnabled={false}
        autoungrabify={false}
        cy={setCy}
      />
      {tooltipState && (
        <NodeTooltip
          content={tooltipState.content}
          position={tooltipState.position}
          visible={tooltipState.visible}
          onClose={() => hideTooltip()}
          cytoscapeCoreRefObject={cytoscapeCoreRefObject}
        />
      )}
    </div>
  );
};

// メモ化のための比較関数
function areEqual(
  prevProps: NetworkContainerProps,
  nextProps: NetworkContainerProps
) {
  const prevNodes = prevProps.filteredData?.nodes ?? [];
  const nextNodes = nextProps.filteredData?.nodes ?? [];
  const prevEdges = prevProps.filteredData?.edges ?? [];
  const nextEdges = nextProps.filteredData?.edges ?? [];

  const nodesEqual =
    prevNodes.length === nextNodes.length &&
    prevNodes.every((n, i) => n.id === nextNodes[i].id);

  const edgesEqual =
    prevEdges.length === nextEdges.length &&
    prevEdges.every((e, i) => e.id === nextEdges[i].id);

  return (
    nodesEqual &&
    edgesEqual &&
    prevProps.selectedNodeIds === nextProps.selectedNodeIds &&
    prevProps.cytoscapeCoreRefObject === nextProps.cytoscapeCoreRefObject
  );
}

export default React.memo(NetworkContainer, areEqual);
